import { memo, useCallback } from 'react';
import { Button, IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

/** Icon color that goes with each box tint. */
const ICON_TONE = Object.freeze({
  Primary: 'PrimaryAlt',
  Success: 'Green',
  Purple: 'Purple',
  LightBlue: 'LightBlue',
  LimeGreen: 'LimeGreen',
  Yellow: 'Yellow',
});

/**
 * One summary block of the Review step with its Edit action.
 *
 * @param {object} props
 * @param {import('lucide-react').LucideIcon} props.icon
 * @param {'Primary'|'Success'|'Purple'|'LightBlue'|'LimeGreen'|'Yellow'} [props.tone='Primary'] Tint of the icon box.
 * @param {string} props.title
 * @param {string} [props.stepId] Step Edit returns to; omit for a section that is not editable.
 * @param {(stepId: string) => void} [props.onEdit]
 */
function ReviewSection({ icon, tone = 'Primary', title, stepId, onEdit, children }) {
  const { t } = useTranslation('people-access');
  const handleEdit = useCallback(() => onEdit(stepId), [onEdit, stepId]);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-edge bg-surface p-4">
      <IconHighlight icon={icon} size="small" bgColor={tone} iconColor={ICON_TONE[tone]} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-content">{title}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5 text-sm text-content-muted">{children}</div>
      </div>
      {stepId && (
        <Button variant="ghost" size="sm" onClick={handleEdit}>
          {t('invite.actions.edit')}
        </Button>
      )}
    </div>
  );
}

export default memo(ReviewSection);
